import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { Dialog } from 'primereact/dialog';
import { Nullable } from 'primereact/ts-helpers';
import { Messages } from 'primereact/messages';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCalculator } from '@fortawesome/free-solid-svg-icons';
import InfoButton from '@/components/InfoAlertActions/InfoAlertActions';
import { useMessage, useToastNotification } from '@/hooks';

import {
  InputDateField,
  InputTextField,
  Button,
  LoadingSpinner,
} from '@/components';
import CheckBox from '@/components/CheckBox/CheckBox';
import { queryClient } from '@/providers/AppProvider';
import { cambiarFechaServicioTerminal } from '@/services/cambiarFechaServicioTerminal';
import { ultimoServicioTerminal } from '@/services/ultimoServicioTerminal';
import { modificarFechaRetiro } from '@/services/modificarFechaRetiro';
import { cambiarEstadoPaga } from '@/services/cambiarEstadoPaga';
import ZeroCostConfirmationDialog from '@/components/ZeroCostConfirmationDialog/ZeroCostConfirmationDialog';
import { getAccessToken } from '@/utils/auth';
import { decodeJWT } from '@/utils/decodeJWT';

import PagesPaths from '@/enums/PagesPaths';
import { getVisitasLiquidadas } from '../Depo20/services/getVisitasLiquidadas';
import {
  saveAndAddSettlement,
  SaveSettlementValidated,
} from '../Depo20/services/saveAndAddSettlement';

import { getLiquidateContainerBl } from '../Depo20/services/getLiquidateContainerBl';
import { getOidVisita } from './services/getOidVisita';
import { getOidVisitaAbierta } from './services/getOidVisitaAbierta';
import { addContainerBlImport } from '../Depo20/services/addContainerBlImport';
import { addContainerBlVacioImport } from './services/addContainerBlVacioImport';
import { liquidarRecepcionVacioImport } from './services/liquidarRecepcionVacioImport';
import InvoiceDetail from './InvoiceDetail';
import styles from './AddContainerModalImportFCL.module.scss';
import { containerIsImo } from './services/containerIsImo';
import { visitaEstaEnStock } from './services/visitaEstaEnStock';
import { ServicioTerminalData } from './types/servicioTerminalTypes';

type VacioImportacionContainerData = {
  numero: string;
  marca: string;
  iso: string | null;
  tamano: number;
  tipo: string;
  armador: string;
  oidVisita: number;
  playa: string | null;
  llego: string;
  isSuccess: boolean;
  exception: {
    message: string;
  };
};

type ModalProps = {
  setVisibility: (visibility: boolean | ((prev: boolean) => boolean)) => void;
  visible: boolean;
  bodyOnSave: SaveSettlementValidated;
  oidLiquidacion: number;
  rut: string;
  onLoadingChange?: (loading: boolean) => void;
  onZeroCostDialogChange?: (isOpen: boolean) => void;
  tipo?: 'Import' | 'Export' | 'VacioImportacion';
};

const regexValidate = /^[A-Za-z]{4}\d{7}$/;

export const AddContainerModalImportFCL: FC<ModalProps> = ({
  setVisibility,
  visible,
  bodyOnSave,
  oidLiquidacion,
  rut,
  onLoadingChange,
  onZeroCostDialogChange,
  tipo,
}) => {
  const { t } = useTranslation(['importFCL', 'exportFCL']);

  const { showToast } = useToastNotification();
  const { showMessage, elementRef, clearMessages } = useMessage();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    showMessage: showWarningMessage,
    elementRef: elementRefWarning,
    clearMessages: clearWarningMessage,
  } = useMessage();

  const [formValue, setFormValue] = useState({
    contenedor: '',
    fechaRetiro: null,
    entregaDirecta: false,
  });

  const [showZeroCostDialog, setShowZeroCostDialog] = useState(false);

  useEffect(() => {
    if (onZeroCostDialogChange) {
      onZeroCostDialogChange(showZeroCostDialog);
    }
  }, [showZeroCostDialog, onZeroCostDialogChange]);
  const [servicioTerminalData, setServicioTerminalData] =
    useState<ServicioTerminalData | null>(null);
  const [vacioImportCostCalculated, setVacioImportCostCalculated] =
    useState(false);

  // Reset cost calculation state when container changes
  useEffect(() => {
    setVacioImportCostCalculated(false);
  }, [formValue.contenedor]);

  const toggleDialog = useCallback(() => {
    setVisibility((isVisible: boolean) => !isVisible);
    // Reset state when closing modal
    if (visible) {
      setVacioImportCostCalculated(false);
    }
  }, [setVisibility, visible]);

  const showMessageError = useCallback(
    (message: string, title?: string) => {
      showMessage({
        severity: 'error',
        detail: message,
        summary: title,
      });
    },
    [showMessage]
  );

  const showMessageWarning = useCallback(
    (message: string, title?: string) => {
      showWarningMessage({
        severity: 'warn',
        detail: title || '',
        summary: message,
      });
    },
    [showWarningMessage]
  );

  const calculateAmount = useCallback(
    async (data: { oidVisita: number }) => {
      const { oidVisita } = data;
      const { entregaDirecta, fechaRetiro } = formValue;

      const body = {
        oidVisita,
        fechaRetiro: fechaRetiro || '',
        entregaDirecta,
        rut,
      };
      const res = await getLiquidateContainerBl(body);

      return res;
    },
    [formValue, rut]
  );

  const {
    mutate: validateContainer,
    data: isImo,
    isPending: validatingContainer,
  } = useMutation({
    mutationFn: containerIsImo,
  });

  const {
    mutate: checkVisitaEnStock,
    data: visitaEnStock,
    isPending: checkingVisitaEnStock,
  } = useMutation({
    mutationFn: visitaEstaEnStock,
    onSuccess: (data) => {
      if (data === false) {
        showMessageError(
          'No se encuentra el contenedor, por favor contáctese con Atención al Cliente.'
        );
      }
    },
  });

  const {
    mutate: getContainerData,
    data: containerData,
    isPending: gettingContainerData,
  } = useMutation({
    mutationFn: (query: { marca: string; numero: string }) =>
      tipo === 'VacioImportacion'
        ? getOidVisitaAbierta(query)
        : getOidVisita(query),
    onError: (error) => {
      clearMessages();
      clearWarningMessage();
      showMessageError(error?.message);
    },
    onSuccess: (data) => {
      clearMessages();
      clearWarningMessage();

      if (data?.playa === null && data?.llego === 'true') {
        return showMessageError(t('importFCL:containerDelivered'));
      }

      if (tipo === 'VacioImportacion') {
        const containerString = formValue.contenedor;
        const marca = containerString.substring(0, 4);
        const numero = containerString.substring(4);
        return checkVisitaEnStock({ marca, numero });
      }

      return validateContainer(data);
    },
  });

  const {
    mutate: calculateAmountMutation,
    data: amountData,
    isSuccess,
    isPending: calculatingAmount,
  } = useMutation({
    mutationFn: calculateAmount,
    onError: (error) => {
      clearMessages();
      clearWarningMessage();
      showMessageError(error?.message);
    },
    onSuccess: async (result) => {
      const totalCost = result?.total;

      if (totalCost <= 0) {
        if (containerData) {
          try {
            const servicioTerminalResult = await ultimoServicioTerminal({
              oidVisita: containerData.oidVisita,
            });
            setServicioTerminalData(servicioTerminalResult);
          } catch (error) {
            showToast({
              severity: 'error',
              detail: t('importFCL:errorChangingStatus'),
            });
          }
        }
        setShowZeroCostDialog(true);
      }
    },
  });

  const { mutate: addContainerMutation, isPending: addingContainer } =
    useMutation({
      mutationFn: async (params: {
        currentOidLiquidacion: number;
        isFromZeroCostDialog?: boolean;
      }) => {
        if (!containerData) return undefined;

        if (tipo === 'VacioImportacion') {
          const visitaData =
            containerData as unknown as VacioImportacionContainerData;

          const accessToken = getAccessToken();
          const decodedToken = accessToken ? decodeJWT(accessToken) : null;
          const rutTitular =
            decodedToken?.ultimo_rut_cliente_liquidacion_electronica_vacio_import ||
            '';

          const visita = {
            numero: visitaData.numero,
            marca: visitaData.marca,
            isoCodigo: visitaData.iso || '',
            isoTamano: visitaData.tamano,
            isoTipo: visitaData.tipo || '',
            noHacerAltaLibre: false,
            tieneCertificadoImo: false,
            notificacionImo: false,
          };

          const body = {
            visitas: [visita],
            codigoArmador: visitaData.armador,
            tipoDeDocumento: 'C',
            esProforma: false,
            rutTitular,
            liquidando: true,
            lavado: '',
            oidLiquidacion: params.currentOidLiquidacion,
          };

          await addContainerBlVacioImport(body);
        } else {
          const { oidVisita } = containerData;
          const { entregaDirecta, fechaRetiro } = formValue;

          const body = {
            oidVisita,
            fechaRetiro: fechaRetiro ?? '',
            entregaDirecta,
            oidLiquidacion: params.currentOidLiquidacion,
          };
          await addContainerBlImport(body);
        }

        return params.isFromZeroCostDialog;
      },
      onError: (error) => {
        clearMessages();
        clearWarningMessage();

        showToast({
          severity: 'error',
          detail: error?.message ?? t('importFCL:errorAddingContainers'),
        });

        showMessageError(
          error?.message ?? t('importFCL:errorAddingContainers')
        );
      },
      onSuccess: async (isFromZeroCostDialog) => {
        clearMessages();
        clearWarningMessage();
        if (onLoadingChange) onLoadingChange(false);
        queryClient.invalidateQueries({
          queryKey: ['getImportFCLTable', oidLiquidacion],
        });
        showToast({
          severity: 'success',
          detail: t('importFCL:containerAdded'),
        });
        queryClient.invalidateQueries({
          queryKey: ['getImportFCLTable'],
        });
        if (!isFromZeroCostDialog) {
          toggleDialog();
        }
        if (isFromZeroCostDialog) {
          try {
            const queryParams = new URLSearchParams(location.search);
            const urlOidLiquidacion = parseInt(
              queryParams.get('id') ?? '0',
              10
            );
            const currentOidLiquidacion = oidLiquidacion || urlOidLiquidacion;
            const visitas = await getVisitasLiquidadas(currentOidLiquidacion);
            const containerCount = visitas?.length || 0;

            if (
              (containerCount === 0 || containerCount === 1) &&
              currentOidLiquidacion
            ) {
              await cambiarEstadoPaga({
                oidLiquidacion: currentOidLiquidacion,
              });
              setShowZeroCostDialog(false);
              navigate(PagesPaths.mySettlement);
            } else {
              toggleDialog();
            }
          } catch (error) {
            showToast({
              severity: 'error',
              detail: t('importFCL:errorChangingStatus'),
            });
            toggleDialog();
          }
        }
      },
    });

  const { mutateAsync: saveData } = useMutation({
    mutationFn: saveAndAddSettlement,
  });

  const proceedWithAddContainer = async (
    settlementAlreadyCreated?: number,
    isFromZeroCostDialog?: boolean
  ) => {
    if (!containerData) return;

    const queryParams = new URLSearchParams(location.search);
    const urlOidLiquidacion = parseInt(queryParams.get('id') ?? '0', 10);
    const currentOidLiquidacion =
      oidLiquidacion || urlOidLiquidacion || settlementAlreadyCreated;

    if (!currentOidLiquidacion) {
      try {
        const settlementId = await saveData(bodyOnSave);
        addContainerMutation({
          currentOidLiquidacion: settlementId,
          isFromZeroCostDialog,
        });
        const newPath = `${location.pathname}?id=${settlementId}`;
        navigate(newPath, { replace: true });
        showToast({
          severity: 'success',
          detail: t('importFCL:settlementSaved'),
        });
      } catch (error) {
        showToast({
          severity: 'error',
          detail: t('importFCL:settlementNotSaved'),
        });
      }
      return;
    }

    addContainerMutation({ currentOidLiquidacion, isFromZeroCostDialog });
  };

  const {
    data: liquidarRecepcionVacioImportData,
    mutate: liquidarRecepcionVacioImportMutation,
    isPending: liquidatingRecepcion,
  } = useMutation({
    mutationFn: async () => {
      if (!containerData || tipo !== 'VacioImportacion') return undefined;

      const visitaData =
        containerData as unknown as VacioImportacionContainerData;
      const accessToken = getAccessToken();
      const decodedToken = accessToken ? decodeJWT(accessToken) : null;
      const rutTitular =
        decodedToken?.ultimo_rut_cliente_liquidacion_electronica_vacio_import ||
        '';

      const visita = {
        numero: visitaData.numero,
        marca: visitaData.marca,
        isoCodigo: visitaData.iso || '',
        isoTamano: visitaData.tamano,
        isoTipo: visitaData.tipo || '',
        noHacerAltaLibre: false,
        tieneCertificadoImo: false,
        notificacionImo: false,
      };

      const body = {
        visitas: [visita],
        codigoArmador: visitaData.armador,
        tipoDeDocumento: 'C',
        esProforma: false,
        rutTitular,
        liquidando: true,
        lavado: '',
      };

      return liquidarRecepcionVacioImport(body);
    },
    onError: (error) => {
      clearMessages();
      clearWarningMessage();
      showMessageError(error?.message ?? t('importFCL:errorCalculatingCost'));
    },
    onSuccess: () => {
      clearMessages();
      clearWarningMessage();
      setVacioImportCostCalculated(true);
    },
  });

  const { mutate: changeDateMutation, isPending: changingDate } = useMutation({
    mutationFn: cambiarFechaServicioTerminal,
    onSuccess: async (result) => {
      try {
        if (
          result &&
          typeof result === 'string' &&
          (result as string).trim() !== ''
        ) {
          setShowZeroCostDialog(false);
          showToast({
            severity: 'error',
            detail: t('importFCL:cannotChangePickupDate'),
          });
          return;
        }

        if (!containerData || !formValue.fechaRetiro) return;

        const fechaRetiroValue = formValue.fechaRetiro;
        const fechaRetiroString =
          fechaRetiroValue &&
          typeof fechaRetiroValue === 'object' &&
          'toISOString' in fechaRetiroValue
            ? (fechaRetiroValue as Date).toISOString()
            : String(fechaRetiroValue);

        const queryParams = new URLSearchParams(location.search);
        const urlOidLiquidacion = parseInt(queryParams.get('id') ?? '0', 10);
        const currentOidLiquidacion = oidLiquidacion || urlOidLiquidacion;

        if (!currentOidLiquidacion) {
          try {
            const settlementId = await saveData(bodyOnSave);
            const newPath = `${location.pathname}?id=${settlementId}`;
            navigate(newPath, { replace: true });

            const modifyResult = await modificarFechaRetiro({
              oidVisita: containerData.oidVisita,
              nuevaFecha: fechaRetiroString,
              oidLiquidacion: settlementId,
            });

            if (
              modifyResult &&
              typeof modifyResult === 'string' &&
              (modifyResult as string).trim() !== ''
            ) {
              setShowZeroCostDialog(false);
              showToast({
                severity: 'error',
                detail: t('importFCL:cannotChangePickupDate'),
              });
              return;
            }

            const visitas = await getVisitasLiquidadas(settlementId);
            const containerCount = visitas?.length || 0;
            if (containerCount === 0 || containerCount === 1) {
              proceedWithAddContainer(settlementId, true);
            } else {
              setShowZeroCostDialog(false);
              navigate(PagesPaths.mySettlement);
            }
            return;
          } catch (error) {
            setShowZeroCostDialog(false);
            showToast({
              severity: 'error',
              detail: t('importFCL:settlementNotSaved'),
            });
            return;
          }
        }

        const modifyResult = await modificarFechaRetiro({
          oidVisita: containerData.oidVisita,
          nuevaFecha: fechaRetiroString,
          oidLiquidacion: currentOidLiquidacion,
        });

        if (
          modifyResult &&
          typeof modifyResult === 'string' &&
          (modifyResult as string).trim() !== ''
        ) {
          setShowZeroCostDialog(false);
          showToast({
            severity: 'error',
            detail: t('importFCL:cannotChangePickupDate'),
          });
          return;
        }

        const visitas = await getVisitasLiquidadas(currentOidLiquidacion);
        const containerCount = visitas?.length || 0;

        if (containerCount === 0) {
          proceedWithAddContainer(undefined, true);
        } else {
          showToast({
            severity: 'error',
            detail: t('importFCL:errorAddingContainers'),
          });
          setShowZeroCostDialog(false);
        }
      } catch (error) {
        setShowZeroCostDialog(false);
        showToast({
          severity: 'error',
          detail:
            (error as Error)?.message ?? t('importFCL:errorAddingContainers'),
        });
      }
    },
    onError: (error) => {
      setShowZeroCostDialog(false);
      showToast({
        severity: 'error',
        detail: error?.message ?? t('importFCL:errorAddingContainers'),
      });
    },
  });

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(addingContainer || liquidatingRecepcion);
    }
  }, [addingContainer, liquidatingRecepcion, onLoadingChange]);

  const handleFieldChange = useCallback(
    (value: string | Nullable<Date> | boolean, name: string) => {
      if (name === 'fechaRetiro') {
        clearMessages();
        clearWarningMessage();
      }

      setFormValue((currentValue) => ({
        ...currentValue,
        [name]: value,
      }));
    },
    [clearMessages, clearWarningMessage]
  );

  const onCalculateButtonClick = useCallback(() => {
    clearWarningMessage();
    if (!containerData) return;

    if (tipo === 'VacioImportacion' && visitaEnStock === true) {
      // First calculate the cost, then add to liquidation
      liquidarRecepcionVacioImportMutation();
      return;
    }

    calculateAmountMutation(containerData);
  }, [
    containerData,
    calculateAmountMutation,
    clearWarningMessage,
    tipo,
    visitaEnStock,
    liquidarRecepcionVacioImportMutation,
  ]);

  const onContainerInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let { value } = e.target;
      value = value?.replace(/[^A-Za-z0-9]/g, '');

      if (value?.length > 11) return;

      const marca = value.slice(0, 4);
      const numero = value.slice(4, 11);

      if (marca && !marca.match(/^[A-Za-z]+$/)) return;
      if (numero && !numero.match(/^[0-9]+$/)) return;

      setFormValue((currentValue) => ({
        ...currentValue,
        contenedor: value?.toUpperCase(),
      }));
    },
    []
  );

  const onAddButtonClick = async () => {
    if (!containerData) return;

    // For VacioImportacion, check if cost has been calculated
    if (tipo === 'VacioImportacion' && !vacioImportCostCalculated) return;

    // For other types, check if amount data exists
    if (tipo !== 'VacioImportacion' && !amountData) return;

    proceedWithAddContainer();
  };

  const handleZeroCostAccept = async () => {
    if (!containerData || !formValue.fechaRetiro || !servicioTerminalData)
      return;

    try {
      const { oidServicioTerminal } = servicioTerminalData;

      const fechaRetiroValue = formValue.fechaRetiro;
      const fechaRetiroString =
        fechaRetiroValue &&
        typeof fechaRetiroValue === 'object' &&
        'toISOString' in fechaRetiroValue
          ? (fechaRetiroValue as Date).toISOString()
          : String(fechaRetiroValue);

      changeDateMutation({
        oidVisita: oidServicioTerminal,
        nuevaFecha: fechaRetiroString,
      });
    } catch (error) {
      setShowZeroCostDialog(false);
      showToast({
        severity: 'error',
        detail:
          (error as Error)?.message ?? t('importFCL:errorAddingContainers'),
      });
    }
  };

  const handleZeroCostCancel = () => {
    setShowZeroCostDialog(false);
  };

  const formatDateForDialog = (
    date: Date | string | null | undefined
  ): string | undefined => {
    if (!date) return undefined;
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  useEffect(() => {
    const container = formValue?.contenedor;

    if (!regexValidate.test(formValue.contenedor)) return;

    const marca = container.slice(0, 4);
    const numero = container.slice(4, 11);

    getContainerData({ marca, numero });
  }, [formValue?.contenedor, getContainerData]);

  useEffect(() => {
    if (amountData?.warning) {
      showMessageWarning(
        `${amountData?.detalle?.split(' - ')[0]} - ${amountData?.warning} `,
        t('importFCL:warning')
      );
    }
  }, [amountData, showMessageWarning, t]);

  const dateIsEnabled =
    !!containerData &&
    ((tipo === 'VacioImportacion' && typeof visitaEnStock === 'boolean') ||
      (tipo !== 'VacioImportacion' && typeof isImo === 'boolean'));

  const isDateRequired = tipo !== 'VacioImportacion';

  const minDate = useMemo(() => {
    const startDate = new Date(1, 0, 1);

    const isImoOrVisitaEnStock =
      tipo === 'VacioImportacion' ? visitaEnStock : isImo;
    if (isImoOrVisitaEnStock) return startDate;
    const types = ['GS', 'IS', 'RF', 'RH'];

    const { tipo: containerTipo } = containerData || {};

    if (types.includes(containerTipo || '')) return startDate;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today;
  }, [containerData, isImo, visitaEnStock, tipo]);

  return (
    <Dialog
      header={
        <div className="text-blue-800">
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
          {t('importFCL:addContainer')}
        </div>
      }
      visible={visible}
      style={{
        maxWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
        minWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
        position: 'relative',
      }}
      onHide={toggleDialog}
      modal
      footer={
        <div className={`p-2 md:p-4 ${styles.contianerButtons}`}>
          <div className="w-ful flex justify-content-end">
            <Button text onClick={toggleDialog} style={{ color: '#170a33' }}>
              {t('importFCL:cancel')}
            </Button>
            {!showZeroCostDialog && (
              <Button
                onClick={onAddButtonClick}
                className={styles.containersButton}
                disabled={
                  (tipo === 'VacioImportacion'
                    ? !vacioImportCostCalculated ||
                      (liquidarRecepcionVacioImportData?.total ?? 0) <= 0
                    : !isSuccess || (amountData?.total ?? 0) <= 0) ||
                  addingContainer ||
                  liquidatingRecepcion ||
                  changingDate
                }
                loading={
                  addingContainer || liquidatingRecepcion || changingDate
                }
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t('importFCL:add')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {(addingContainer ||
        liquidatingRecepcion ||
        calculatingAmount ||
        gettingContainerData ||
        validatingContainer ||
        checkingVisitaEnStock ||
        changingDate) && (
        <div
          style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
          className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="p-4 md:pt-0">
        <div className={`${styles.formContainer} flex-column md:flex-row`}>
          <div
            className={`${styles.form} flex flex-column md:flex-row md:gap-3`}
          >
            <InputTextField
              id="contenedor"
              name="contenedor"
              value={formValue.contenedor}
              onChange={onContainerInputChange}
              label={t('importFCL:containerNumber')}
              className={tipo === 'VacioImportacion' ? 'w-1/2' : 'w-full'}
              inputClassName="h-auto"
            />
            {tipo !== 'VacioImportacion' && (
              <InputDateField
                id="fechaRetiro"
                name="fechaRetiro"
                value={formValue.fechaRetiro}
                onChange={(e) => handleFieldChange(e, 'fechaRetiro')}
                label={t('importFCL:withdrawalDate')}
                className="w-full "
                inputClassName="h-auto"
                disabled={!dateIsEnabled}
                minDate={minDate}
              />
            )}
            {tipo !== 'VacioImportacion' && (
              <CheckBox
                style={{ width: '100%' }}
                className="mt-3 md:mt-0"
                label={t('importFCL:directDelivery')}
                checked={formValue.entregaDirecta}
                onChange={(e) => handleFieldChange(e, 'entregaDirecta')}
              />
            )}
          </div>
          <Button
            type="button"
            onClick={onCalculateButtonClick}
            className="w-full md:w-auto"
            style={{ padding: '0.5rem 1rem' }}
            disabled={
              (!formValue.fechaRetiro && isDateRequired) ||
              !regexValidate.test(formValue.contenedor) ||
              calculatingAmount ||
              gettingContainerData ||
              checkingVisitaEnStock ||
              (tipo === 'VacioImportacion' && visitaEnStock !== true)
            }
          >
            <FontAwesomeIcon icon={faCalculator} className="mr-2" />
            {t('importFCL:calculateAmount')}
          </Button>
        </div>
        {tipo !== 'VacioImportacion' && (
          <InfoButton
            type="info"
            infoText={t('importFCL:addContainerInfoMessage')}
          />
        )}

        <Messages ref={elementRef} />
        <Messages ref={elementRefWarning} />

        <div className="mt-4">
          {!!amountData && amountData?.total && amountData?.total > 0 && (
            <InvoiceDetail data={amountData} />
          )}
          {!!liquidarRecepcionVacioImportData &&
            liquidarRecepcionVacioImportData?.total > 0 && (
              <InvoiceDetail data={liquidarRecepcionVacioImportData} />
            )}
        </div>
      </div>

      <ZeroCostConfirmationDialog
        visible={showZeroCostDialog}
        onAccept={handleZeroCostAccept}
        onCancel={handleZeroCostCancel}
        initialFechaRetiro={formatDateForDialog(
          servicioTerminalData?.hasta || amountData?.fechaRetiroProforma
        )}
        fechaRetiro={formatDateForDialog(formValue?.fechaRetiro)}
      />
    </Dialog>
  );
};

export default AddContainerModalImportFCL;
